import { useState, useEffect } from "react";
import { toast } from "../../../../../components/toastNotification";
import { useRecoilState, useSetRecoilState } from "recoil";
import OfficeStatusForm from "../form/officeStatusForm";
import AddButton from "../../../../../components/AddButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import useOfficeStatus from "../hooks/useOfficeStatus";
import useDeleteOfficeStatus from "../hooks/useDeleteOfficeStatus";
import {
  addStateOfficeStatus,
  selectedRowsStateOfficeStatus,
  editStateOfficeStatus,
  rowEditOfficeStatus,
  officeStatusFetching,
  officeStatusReFetching,
  officeStatusFilterText,
} from "../atoms/atoms";
import TableFilters from "components/TableFilters";
import { PERMISSIONS } from "../../../../PermissionsConstants";
import { useRecoilValue } from "recoil";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import { useLocation } from "react-router-dom";
import EditButton from "../../../../../components/EditButton";
import { OfficeStatusType, OfficeStatusTypeTableData } from "../types";

import Timer from "components/Timer";
import MaterialTableTS from "components/tables/MaterialTableTS";

export default function OfficeStatusTable() {
  const { data: directData, refetch: reFetch } = useOfficeStatus();

  const BulkDelete = useDeleteOfficeStatus();

  //Atoms Call
  const [actionAddState, setActionAddState] =
    useRecoilState(addStateOfficeStatus);
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateOfficeStatus
  );
  const [actionEditState, setActionEditState] = useRecoilState(
    editStateOfficeStatus
  );
  const [filterTextState, setFilterTextState] = useRecoilState(
    officeStatusFilterText
  );
  const setEditRowData = useSetRecoilState(rowEditOfficeStatus);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [isFetching, setIsFetching] = useRecoilState(officeStatusFetching);
  const isReFetching = useRecoilValue(officeStatusReFetching);

  //Local States
  const [tableData, setTableData] = useState<OfficeStatusType[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState<
    OfficeStatusType[]
  >([]);

  // Permission States
  const addPermission =
    PERMISSIONS.permission_utilities_officeconfiguration_officestatus_add;
  const editPermission =
    PERMISSIONS.permission_utilities_officeconfiguration_officestatus_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_officeconfiguration_officestatus_delete;

  // Get Data
  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };

  useEffect(() => {
    reFetch();
  }, [isReFetching]);

  useEffect(() => {
    if (directData?.status === 400) {
      toast.fire("Server Error", directData?.data.Message, "error");
      setTableData([]);
      setIsFetching(false);
    } else {
      Loader();
    }
  }, [directData]);

  //For filtered data update

  // For Recent Activity + Search
  let location = useLocation();
  let recordIdFromRecent;

  const [recent, setRecent] = useState("");
  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      notificationFilterById(
        recordIdFromRecent.recordId,
        recordIdFromRecent.moduleName,
        recordIdFromRecent.subModuleName
      ).then((result: OfficeStatusType[] | undefined) => {
        if (typeof result !== "object") {
          toast.fire("Failed", result, "error");
        } else {
          setTableFilteredData(result);
          setRecent(result[0].status);
        }
      });
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
  }, [recordIdFromRecent]);

  // For Filter/Search
  const filtereditems =
    tableData &&
    tableData?.filter(
      (item) =>
        item.status &&
        item.status
          .toLowerCase()
          .includes(filterTextState?.trim().toLowerCase())
    );

  useEffect(() => {
    if (!recent) setTableFilteredData(filtereditems);
  }, [filterTextState]);

  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows: OfficeStatusTypeTableData[]) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });

    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData: OfficeStatusTypeTableData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    setIsFetching(true);

    const ids = selectedRows && selectedRows.map((row) => row.officeStatusId);

    BulkDelete(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Office Status" : "Office Status"
    }?`}</div>
  );

  const matColumns = [
    {
      title: "Status Name",
      field: "status",
      customSort: (a, b) =>
        a.status && b.status
          ? a.status.trim().localeCompare(b.status.trim())
          : a.status
          ? 0
          : -1,
    },
    {
      title: "Description",
      field: "description",
      customSort: (a, b) =>
        a.description && b.description
          ? a.description.trim().localeCompare(b.description.trim())
          : a.description
          ? 0
          : -1,
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },

    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center mr-5">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="bg-white p-4">
        {actionAddState || actionEditState ? (
          <div>
            <OfficeStatusForm setRecent={setRecent} />
          </div>
        ) : (
          <>
            <div className="bg-white w-full">
              <div className="flex flex-row justify-between items-center">
                <TableFilters
                  defaultText={filterTextState}
                  recentText={recent}
                  setRecent={setRecent}
                  textInputName={"StatusNumber"}
                  columnDetails={"Office Status"}
                  filterText={setFilterTextState}
                  disabledButton={
                    actionEditState ||
                    (selectedRows !== undefined && selectedRows.length > 0)
                  }
                  disabledTextBox={
                    selectedRows !== undefined && selectedRows.length > 0
                  }
                />

                <div className="">
                  {userPermissions.includes(addPermission) ? (
                    <AddButton
                      buttonText="New Office Status"
                      onClick={() => handleAdd()}
                      disabled={
                        actionEditState ||
                        (selectedRows !== undefined && selectedRows.length > 0)
                      }
                    />
                  ) : (
                    ""
                  )}
                </div>
              </div>
              <div>
                {userPermissions.includes(deletePermission) ? (
                  <div className="text-secondary italic text-xs3 my-2">
                    <p className="font-bold italic inline-block mr-1">Note:</p>
                    <span>Tick the box to deactivate some data.</span>
                  </div>
                ) : (
                  <div className="mb-4"></div>
                )}
              </div>

              <div>
                <MaterialTableTS
                  data={
                    isFetching
                      ? []
                      : filterTextState === "" && recent === ""
                      ? tableData
                      : filterTextState === ""
                      ? tableFilteredData
                      : filtereditems
                  }
                  showTitle={true}
                  columns={matColumns}
                  isLoading={isFetching}
                  onSelectionChange={handleSelectedRowsChange}
                  selectedRows={selectedRows}
                  selection={
                    !actionEditState &&
                    !actionAddState &&
                    userPermissions.includes(deletePermission)
                  }
                  setConfirmModalOpen={setOpenModalDelete}
                />
              </div>
            </div>

            <ConfirmBoxDelete
              ModalTitle="Deactivate Office Status"
              qConfirmation={deleteMessage}
              isOpen={openModalDelete}
              onClose={() => {
                setOpenModalDelete(false);
                setSelectedRows([]);
              }}
              onClickFunction={handleDelete}
              cancelButtonRef={undefined}
            />
          </>
        )}
      </div>
    </>
  );
}
